import { useEffect, useRef, useState, type FormEvent, type KeyboardEvent } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '@repletoryreach/sdk-auth';
import { flows } from '@repletoryreach/sdk-api';
import { useAppStore, type Density, type ThemeMode } from '@repletoryreach/sdk-utils/store';
import { Button, Text } from '@repletoryreach/sdk-ui';
import { useT } from '../../i18n/I18nProvider';
import type { Locale } from '../../i18n/catalog';
import { useLocalePreference } from '../../lib/useLocalePreference';
import { ASSISTANT_INTRO, useGuides } from '../guides/GuideProvider';
import { useAssistantContext } from './useAssistantContext';
import { ReplyHtml, inlineActions } from './ReplyHtml';
import './assistant.css';

type ActionState = 'offered' | 'done' | 'declined';
interface Line {
  role: 'person' | 'assistant' | 'note';
  text: string;
  actions?: flows.ConsoleAssistantAction[];
  states?: ActionState[];
}

const STORE = 'tenant.assistant.conversation';
/** Actions that take the person somewhere. Each is a button naming where it goes, so pressing it is the choice. */
const GO = new Set<flows.ConsoleAssistantActionType>(['navigate', 'start_tour', 'open_guide']);
/** The page actions not already shown as a button inside the reply's text. */
const below = (l: Line) => {
  const inline = l.role === 'assistant' ? inlineActions(l.text) : new Set<number>();
  return (l.actions ?? []).map((a, j) => ({ a, j })).filter(({ a, j }) => GO.has(a.type) && !inline.has(j));
};
const readLines = (): Line[] => {
  try { return JSON.parse(window.sessionStorage.getItem(STORE) ?? '[]') as Line[]; } catch { return []; }
};

/**
 * The console assistant (owner's decisions of 8 October 2026): a chat bubble on every page of the console, like the
 * website chat. It knows who is signed in and which page they are on, explains the console, and offers to do safe
 * things (open a page, change the theme, language or density, start a tour or open a guide). Nothing happens until
 * the person presses the action's button. Each message is charged as AI use.
 */
export function ConsoleAssistant() {
  const t = useT();
  const nav = useNavigate();
  const { user } = useAuth();
  const context = useAssistantContext();
  const { startTour, openGuide, tourOpen, seen, markSeen, seenReady } = useGuides();
  const { setLocale } = useLocalePreference();
  const setThemeMode = useAppStore((s) => s.setThemeMode);
  const setDensity = useAppStore((s) => s.setDensity);
  const [open, setOpen] = useState(false);
  const [lines, setLines] = useState<Line[]>(readLines);
  const [draft, setDraft] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<{ text: string; topUp?: boolean } | null>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  // The one-time greeting (owner, 15 October 2026): once per person, a few seconds after a page has drawn and never
  // over a tour. A tour that has already shown the assistant counts as the introduction.
  const [peek, setPeek] = useState(false);
  useEffect(() => {
    if (!user?.id || !seenReady || tourOpen || open || seen(ASSISTANT_INTRO)) return;
    const show = window.setTimeout(() => { setPeek(true); markSeen(ASSISTANT_INTRO); }, 2500);
    return () => window.clearTimeout(show);
  }, [user?.id, seenReady, tourOpen, open, seen, markSeen]);
  useEffect(() => {
    if (!peek) return;
    const hide = window.setTimeout(() => setPeek(false), 12_000);
    return () => window.clearTimeout(hide);
  }, [peek]);
  useEffect(() => { if (open || tourOpen) setPeek(false); }, [open, tourOpen]);

  useEffect(() => { try { window.sessionStorage.setItem(STORE, JSON.stringify(lines.slice(-40))); } catch { /* the conversation is kept for this page only */ } }, [lines]);
  useEffect(() => { listRef.current?.scrollTo({ top: listRef.current.scrollHeight }); }, [lines, busy, open]);
  useEffect(() => { if (open) inputRef.current?.focus(); }, [open]);

  async function send(e?: FormEvent) {
    e?.preventDefault();
    const message = draft.trim();
    if (!message || busy) return;
    const history = lines.filter((l) => l.role !== 'note').slice(-12).map((l) => ({ role: l.role as 'person' | 'assistant', text: l.text }));
    setLines((ls) => [...ls, { role: 'person', text: message }]);
    setDraft('');
    setError(null);
    setBusy(true);
    try {
      const res = (await flows.askConsoleAssistant({ message, history, context: context(), format: 'html' })).data;
      setLines((ls) => [...ls, { role: 'assistant', text: res.reply, actions: res.actions, states: res.actions.map(() => 'offered') }]);
    } catch (err) {
      const status = (err as { status?: number } | null)?.status;
      setError(status === 402 ? { text: t('shell.assistant.noCredit'), topUp: true } : { text: t('shell.assistant.failed') });
    } finally {
      setBusy(false);
    }
  }

  function onKey(e: KeyboardEvent<HTMLTextAreaElement>) {
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); void send(); }
  }

  function settle(line: number, index: number, state: ActionState) {
    setLines((ls) => ls.map((l, i) => i === line ? { ...l, states: (l.states ?? []).map((s, j) => (j === index ? state : s)) } : l));
  }

  function carryOut(line: number, index: number, a: flows.ConsoleAssistantAction) {
    switch (a.type) {
      case 'navigate': if (a.path) nav(a.path); break;
      case 'set_theme': setThemeMode(a.value as ThemeMode); break;
      case 'set_density': setDensity(a.value as Density); break;
      case 'set_language': setLocale(a.value as Locale); break;
      case 'start_tour': if (a.guide) { setOpen(false); startTour(a.guide); } break;
      case 'open_guide': if (a.guide) { setOpen(false); openGuide(a.guide); } break;
    }
    settle(line, index, 'done');
    setLines((ls) => [...ls, { role: 'note', text: t('shell.assistant.done', { action: a.label }) }]);
  }

  // Staff looking in on a business never spend its credit.
  if (user?.impersonated_by) return null;
  const name = user?.display_name?.split(' ')[0] || undefined;
  return (
    <>
      {open && (
        <section className="rr-assistant" role="dialog" aria-label={t('shell.assistant.title')} data-testid="assistant-panel"
          onKeyDown={(e) => { if (e.key === 'Escape') setOpen(false); }}>
          <header className="rr-assistant__head">
            <div>
              <Text role="bodySmall" fw={700}>{t('shell.assistant.title')}</Text>
              <Text role="caption" c="var(--rr-color-text-muted)">{t('shell.assistant.subtitle')}</Text>
            </div>
            <div className="rr-assistant__head-actions">
              {lines.length > 0 && (
                <Button size="xs" variant="subtle" onClick={() => { setLines([]); setError(null); }}>{t('shell.assistant.clear')}</Button>
              )}
              <button type="button" className="rr-assistant__close" aria-label={t('shell.assistant.close')} onClick={() => setOpen(false)}>×</button>
            </div>
          </header>

          <div className="rr-assistant__list" ref={listRef} aria-live="polite">
            {lines.length === 0 && (
              <div className="rr-assistant__hello">
                <Text role="bodySmall">{name ? t('shell.assistant.helloName', { name }) : t('shell.assistant.hello')}</Text>
                <Text role="caption" c="var(--rr-color-text-muted)">{t('shell.assistant.examples')}</Text>
              </div>
            )}
            {lines.map((l, i) => (
              l.role === 'note'
                ? <div key={i} className="rr-assistant__note">{l.text}</div>
                : (
                  <div key={i} className={`rr-assistant__msg rr-assistant__msg--${l.role}`} data-testid={`assistant-${l.role}`}>
                    <div className="rr-assistant__bubble">
                      {l.role === 'assistant' ? (
                        <ReplyHtml html={l.text} onAction={(j) => { const a = l.actions?.[j]; if (a) carryOut(i, j, a); }}
                          canInline={(j) => { const a = l.actions?.[j]; return !!a && GO.has(a.type); }} />
                      ) : l.text}
                    </div>
                    {/* Going somewhere is one press: the button says where (9 October 2026). Buttons the reply put in its
                        text are not repeated here. */}
                    {below(l).length > 0 && (
                      <div className="rr-assistant__go" data-testid="assistant-go">
                        {below(l).map(({ a, j }, k) => (
                          <Button key={j} size="xs" variant={k === 0 ? 'filled' : 'light'} onClick={() => carryOut(i, j, a)}>{a.label}</Button>
                        ))}
                      </div>
                    )}
                    {/* Changing a setting is confirmed first. */}
                    {(l.actions ?? []).map((a, j) => !GO.has(a.type) && (
                      <div key={j} className="rr-assistant__action" data-testid="assistant-action">
                        <Text role="caption" fw={650}>{a.label}</Text>
                        {l.states?.[j] === 'offered' ? (
                          <div className="rr-assistant__action-buttons">
                            <Button size="xs" onClick={() => carryOut(i, j, a)}>{t('shell.assistant.confirm')}</Button>
                            <Button size="xs" variant="subtle" onClick={() => settle(i, j, 'declined')}>{t('shell.assistant.decline')}</Button>
                          </div>
                        ) : (
                          <Text role="caption" c="var(--rr-color-text-muted)">
                            {l.states?.[j] === 'done' ? t('shell.assistant.actionDone') : t('shell.assistant.actionDeclined')}
                          </Text>
                        )}
                      </div>
                    ))}
                  </div>
                )
            ))}
            {busy && <div className="rr-assistant__msg rr-assistant__msg--assistant"><div className="rr-assistant__bubble rr-assistant__typing">{t('shell.assistant.thinking')}</div></div>}
            {error && (
              <div className="rr-assistant__error" role="alert">
                {error.text}{' '}
                {error.topUp && <Link to="/billing/topup" onClick={() => setOpen(false)}>{t('shell.assistant.topUp')}</Link>}
              </div>
            )}
          </div>

          <form className="rr-assistant__form" onSubmit={send}>
            <textarea ref={inputRef} className="rr-assistant__input" rows={2} value={draft} maxLength={1000}
              aria-label={t('shell.assistant.inputLabel')} placeholder={t('shell.assistant.placeholder')}
              onChange={(e) => setDraft(e.currentTarget.value)} onKeyDown={onKey} />
            <Button type="submit" size="sm" loading={busy} disabled={!draft.trim()}>{t('shell.assistant.send')}</Button>
          </form>
          <Text role="caption" c="var(--rr-color-text-muted)" className="rr-assistant__cost">{t('shell.assistant.cost')}</Text>
        </section>
      )}
      <div className={`rr-assistant__dock${open ? ' is-open' : ''}`}>
      {!open && (
        // On hover or focus a short line slides out; the first time, a greeting instead, which opens the assistant.
        peek ? (
          <div className="rr-assistant__hint is-peek" role="status" data-testid="assistant-peek">
            <button type="button" className="rr-assistant__hint-open" onClick={() => setOpen(true)}>
              <strong>{name ? t('shell.assistant.peekTitleName', { name }) : t('shell.assistant.peekTitle')}</strong>
              <span>{t('shell.assistant.peekBody')}</span>
            </button>
            <button type="button" className="rr-assistant__hint-close" aria-label={t('shell.assistant.peekClose')} onClick={() => setPeek(false)}>×</button>
          </div>
        ) : (
          <div className="rr-assistant__hint" aria-hidden="true" data-testid="assistant-hint">{t('shell.assistant.hint')}</div>
        )
      )}
      <button type="button" className={`rr-assistant__launcher${peek ? ' is-peek' : ''}`} data-guide="shell-assistant" data-testid="assistant-launcher"
        aria-label={open ? t('shell.assistant.close') : t('shell.assistant.open')} aria-expanded={open} onClick={() => setOpen((o) => !o)}>
        {open ? (
          <svg viewBox="0 0 24 24" width="22" height="22" aria-hidden="true"><path d="M6 6l12 12M18 6L6 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" /></svg>
        ) : (
          <svg viewBox="0 0 24 24" width="24" height="24" aria-hidden="true">
            <path d="M4 5.5A2.5 2.5 0 0 1 6.5 3h11A2.5 2.5 0 0 1 20 5.5v8a2.5 2.5 0 0 1-2.5 2.5H10l-4.2 3.6c-.5.4-1.3.1-1.3-.6V16A2.5 2.5 0 0 1 4 13.5z" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round" />
            <path d="M12 6.5l.8 1.9 1.9.8-1.9.8-.8 1.9-.8-1.9-1.9-.8 1.9-.8z" fill="currentColor" />
          </svg>
        )}
      </button>
      </div>
    </>
  );
}
